import { useState, useEffect } from 'react';
import { addPatient, addLog, getEventById, getAllPatientIds } from '../../../utils/storage';
import { getUser } from '../../../utils/auth';
import { getActiveEventId } from '../../../utils/event';
import { getDeviceInfo, getIPAddress } from '../../../utils/device';

type PatientType = 'student' | 'faculty' | 'other';

export default function RegistrationForm() {
  const user = getUser();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    gender: 'Male' as 'Male' | 'Female' | 'Other',
    dateOfBirth: '',
    bloodGroup: '',
    email: '',
    contactNumber: '',
    whatsappNumber: '',
    address: '',
    college: '',
    department: '',
    yearDesignation: '',
  });

  const [patientType, setPatientType] = useState<PatientType>('student');
  const [collegeOption, setCollegeOption] = useState<'sona' | 'other'>('sona');
  const [studentYear, setStudentYear] = useState('');
  const [designation, setDesignation] = useState('');
  const [otherCollege, setOtherCollege] = useState('');
  const [otherDepartment, setOtherDepartment] = useState('');
  const [otherBloodGroup, setOtherBloodGroup] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [registrationFee, setRegistrationFee] = useState<number | null>(null);

  useEffect(() => {
    const loadFee = async () => {
      try {
        const ev = await getEventById(getActiveEventId());
        setRegistrationFee(ev && typeof ev.fee === 'number' ? ev.fee : 0);
      } catch {
        setRegistrationFee(null);
      }
    };
    loadFee();
  }, []);

  const calculateAge = (dob: string): number => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const generatePatientId = async (): Promise<string> => {
    const allIds = await getAllPatientIds();
    const medsparkzIds = allIds.filter((id) => id.startsWith('MEDSPARKZ2601-'));

    if (medsparkzIds.length === 0) {
      return 'MEDSPARKZ2601-0001';
    }

    const maxNumber = medsparkzIds.reduce((max, id) => {
      const parts = id.split('-');
      const num = parseInt(parts[parts.length - 1], 10);
      return Number.isFinite(num) && num > max ? num : max;
    }, 0);

    const nextNumber = maxNumber + 1;
    return `MEDSPARKZ2601-${nextNumber.toString().padStart(4, '0')}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.bloodGroup === 'Other' && !otherBloodGroup.trim()) {
      newErrors.bloodGroup = 'Please specify blood group';
    }
    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^\d{10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Contact number must be 10 digits';
    }

    // College/Organisation validation
    if (patientType === 'student') {
      if (collegeOption === 'other' && !otherCollege.trim()) {
        newErrors.college = 'College name is required';
      }
      if (!studentYear) {
        newErrors.yearDesignation = 'Year is required';
      }
    } else if (patientType === 'faculty') {
      if (collegeOption === 'other' && !otherCollege.trim()) {
        newErrors.college = 'College name is required';
      }
      if (!designation.trim()) {
        newErrors.yearDesignation = 'Designation is required';
      }
    } else {
      // other
      if (!otherCollege.trim()) {
        newErrors.college = 'Organisation name is required';
      }
      if (!formData.yearDesignation.trim()) {
        newErrors.yearDesignation = 'Designation is required';
      }
    }

    // Department is optional. Only require a value when a custom "Other" is explicitly chosen.
    if (patientType !== 'other' && formData.department === 'Other' && !otherDepartment.trim()) {
      newErrors.department = 'Please specify department';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user) return;

    setSubmitting(true);

    try {
      const age = calculateAge(formData.dateOfBirth);
      const patientId = await generatePatientId();

      // Determine final college/organisation value
      let finalCollege = '';
      if (patientType === 'other') {
        finalCollege = otherCollege;
      } else {
        finalCollege = collegeOption === 'sona' ? 'SONA COLLEGE OF TECHNOLOGY' : otherCollege;
      }

      // Determine final year/designation
      let finalYearDesignation = '';
      if (patientType === 'student') {
        finalYearDesignation = studentYear;
      } else if (patientType === 'faculty') {
        finalYearDesignation = designation;
      } else {
        finalYearDesignation = formData.yearDesignation;
      }

      // Determine final department
      let finalDepartment = '';
      if (patientType === 'other') {
        finalDepartment = otherDepartment;
      } else {
        finalDepartment = formData.department === 'Other' ? otherDepartment : formData.department;
      }

      // Determine final blood group
      const finalBloodGroup = formData.bloodGroup === 'Other' ? otherBloodGroup : formData.bloodGroup;

      const newPatient = {
        id: patientId,
        name: formData.name,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth || '',
        age,
        bloodGroup: finalBloodGroup,
        email: formData.email || undefined,
        contactNumber: formData.contactNumber,
        whatsappNumber: formData.whatsappNumber || undefined,
        address: formData.address || undefined,
        college: finalCollege,
        department: finalDepartment,
        yearDesignation: finalYearDesignation,
        registeredAt: new Date().toISOString(),
        registeredBy: user.username,
        paymentStatus: 'unpaid' as const,
        patientType,
        studentYear: patientType === 'student' ? studentYear : undefined,
        designation: patientType === 'faculty' ? designation : undefined,
      };

      await addPatient(newPatient);

      const { browser, device } = getDeviceInfo();
      const ipAddress = await getIPAddress();

      await addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'Patient Registration',
        details: `Registered new patient: ${formData.name} (${patientId}) - ${patientType}`,
        ipAddress,
        deviceName: device,
        browser,
        timestamp: new Date().toISOString(),
      });

      setSuccessMessage(`Patient registered successfully! Patient ID: ${patientId}`);

      // Reset form
      setFormData({
        name: '',
        gender: 'Male',
        dateOfBirth: '',
        bloodGroup: '',
        email: '',
        contactNumber: '',
        whatsappNumber: '',
        address: '',
        college: '',
        department: '',
        yearDesignation: '',
      });
      setPatientType('student');
      setCollegeOption('sona');
      setStudentYear('');
      setDesignation('');
      setOtherCollege('');
      setOtherDepartment('');
      setOtherBloodGroup('');
      setErrors({});

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Registration error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const finalValue = name === 'name' ? value.toUpperCase() : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
          <i className="ri-user-add-line text-teal-600 text-xl"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">New Patient Registration</h3>
          <p className="text-sm text-gray-500">Fill in the patient details below</p>
        </div>
      </div>

      {successMessage && (
        <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-base"></i>
          {successMessage}
        </div>
      )}

      {registrationFee !== null && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm flex items-center gap-2 border ${
          registrationFee > 0
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          <i className={`${registrationFee > 0 ? 'ri-money-rupee-circle-line' : 'ri-ticket-2-line'} text-base`}></i>
          {registrationFee > 0 ? `Registration Fee for this event: ₹${registrationFee}` : 'This event has free registration.'}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Patient Type Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Patient Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setPatientType('student')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                patientType === 'student'
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-graduation-cap-line mr-1.5"></i>Student
            </button>
            <button
              type="button"
              onClick={() => setPatientType('faculty')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                patientType === 'faculty'
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-user-star-line mr-1.5"></i>Faculty
            </button>
            <button
              type="button"
              onClick={() => setPatientType('other')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                patientType === 'other'
                  ? 'bg-white text-teal-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-user-line mr-1.5"></i>Other
            </button>
          </div>
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm uppercase ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter full name"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer"
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blood Group
            </label>
            <select
              name="bloodGroup"
              value={formData.bloodGroup}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer ${
                errors.bloodGroup ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select blood group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="Other">Other</option>
            </select>
            {errors.bloodGroup && <p className="text-xs text-red-500 mt-1">{errors.bloodGroup}</p>}
          </div>
        </div>

        {/* Other Blood Group Input */}
        {formData.bloodGroup === 'Other' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specify Blood Group <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={otherBloodGroup}
              onChange={(e) => {
                setOtherBloodGroup(e.target.value);
                if (errors.bloodGroup) setErrors((prev) => ({ ...prev, bloodGroup: '' }));
              }}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                errors.bloodGroup ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter blood group"
            />
          </div>
        )}

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              maxLength={10}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                errors.contactNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="10-digit mobile number"
            />
            {errors.contactNumber && (
              <p className="text-xs text-red-500 mt-1">{errors.contactNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
            <input
              type="tel"
              name="whatsappNumber"
              value={formData.whatsappNumber}
              onChange={handleChange}
              maxLength={10}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              placeholder="10-digit WhatsApp number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
              placeholder="Enter address"
            />
          </div>
        </div>

        {/* College/Organisation & Department */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* College/Organisation field - different for each type */}
          {patientType !== 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                College <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setCollegeOption('sona')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    collegeOption === 'sona'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  SONA COLLEGE OF TECHNOLOGY
                </button>
                <button
                  type="button"
                  onClick={() => setCollegeOption('other')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    collegeOption === 'other'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Others
                </button>
              </div>
              {collegeOption === 'other' && (
                <>
                  <input
                    type="text"
                    value={otherCollege}
                    onChange={(e) => {
                      setOtherCollege(e.target.value);
                      if (errors.college) setErrors((prev) => ({ ...prev, college: '' }));
                    }}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                      errors.college ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter college name"
                  />
                  {errors.college && <p className="text-xs text-red-500 mt-1">{errors.college}</p>}
                </>
              )}
            </div>
          )}

          {patientType === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={otherCollege}
                onChange={(e) => {
                  setOtherCollege(e.target.value);
                  if (errors.college) setErrors((prev) => ({ ...prev, college: '' }));
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                  errors.college ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter organisation name"
              />
              {errors.college && <p className="text-xs text-red-500 mt-1">{errors.college}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            {patientType === 'other' ? (
              <input
                type="text"
                value={otherDepartment}
                onChange={(e) => {
                  setOtherDepartment(e.target.value);
                  if (errors.department) setErrors((prev) => ({ ...prev, department: '' }));
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter department"
              />
            ) : (
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select department</option>
                <option value="ADS">ADS</option>
                <option value="AIML">AIML</option>
                <option value="BME">BME</option>
                <option value="CIVIL">CIVIL</option>
                <option value="CSD">CSD</option>
                <option value="CSE">CSE</option>
                <option value="ECE">ECE</option>
                <option value="EEE">EEE</option>
                <option value="EFE">EFE</option>
                <option value="EXE">EXE</option>
                <option value="FT">FT</option>
                <option value="IT">IT</option>
                <option value="MCT">MCT</option>
                <option value="MECH">MECH</option>
                <option value="Other">Other</option>
              </select>
            )}
            {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department}</p>}
          </div>
        </div>

        {/* Other Department Input for student/faculty */}
        {patientType !== 'other' && formData.department === 'Other' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specify Department <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={otherDepartment}
              onChange={(e) => {
                setOtherDepartment(e.target.value);
                if (errors.department) setErrors((prev) => ({ ...prev, department: '' }));
              }}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                errors.department ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter department name"
            />
          </div>
        )}

        {/* Year/Designation - different for each type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {patientType === 'student' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                value={studentYear}
                onChange={(e) => {
                  setStudentYear(e.target.value);
                  if (errors.yearDesignation)
                    setErrors((prev) => ({ ...prev, yearDesignation: '' }));
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm bg-white cursor-pointer ${
                  errors.yearDesignation ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select year</option>
                <option value="I">I</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
                <option value="Others">Others</option>
              </select>
              {errors.yearDesignation && (
                <p className="text-xs text-red-500 mt-1">{errors.yearDesignation}</p>
              )}
            </div>
          )}

          {patientType === 'faculty' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={designation}
                onChange={(e) => {
                  setDesignation(e.target.value);
                  if (errors.yearDesignation)
                    setErrors((prev) => ({ ...prev, yearDesignation: '' }));
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                  errors.yearDesignation ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Assistant Professor, Professor"
              />
              {errors.yearDesignation && (
                <p className="text-xs text-red-500 mt-1">{errors.yearDesignation}</p>
              )}
            </div>
          )}

          {patientType === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="yearDesignation"
                value={formData.yearDesignation}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm ${
                  errors.yearDesignation ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter designation"
              />
              {errors.yearDesignation && (
                <p className="text-xs text-red-500 mt-1">{errors.yearDesignation}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-70 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Registering...</>
            ) : (
              <><i className="ri-save-line"></i> Register Patient</>
            )}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              setFormData({
                name: '',
                gender: 'Male',
                dateOfBirth: '',
                bloodGroup: '',
                email: '',
                contactNumber: '',
                whatsappNumber: '',
                address: '',
                college: '',
                department: '',
                yearDesignation: '',
              });
              setPatientType('student');
              setCollegeOption('sona');
              setStudentYear('');
              setDesignation('');
              setOtherCollege('');
              setOtherDepartment('');
              setOtherBloodGroup('');
              setErrors({});
            }}
            className="px-6 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
}